import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Wind, Smile, Frown, Meh, Heart, CircleAlert, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const MOODS = [
  { key: "great", label: "Great", Icon: Sparkles, bg: "bg-accent" },
  { key: "good", label: "Good", Icon: Smile, bg: "bg-sage" },
  { key: "okay", label: "Okay", Icon: Meh, bg: "bg-secondary" },
  { key: "low", label: "Low", Icon: Frown, bg: "bg-muted" },
  { key: "struggling", label: "Struggling", Icon: CircleAlert, bg: "bg-card" },
];

const NATURE_IMG = "https://images.pexels.com/photos/37887523/pexels-photo-37887523.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function WellnessPage() {
  const [data, setData] = useState(null);
  const [picking, setPicking] = useState(false);
  const [note, setNote] = useState("");
  const [chosen, setChosen] = useState(null);
  const [breath, setBreath] = useState(false);

  const load = async () => { const r = await api.get("/wellness/dashboard"); setData(r.data); };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!picking && !breath) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setPicking(false);
        setBreath(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [picking, breath]);

  const submitMood = async () => {
    if (!chosen) return;
    try { await api.post("/wellness/mood", { mood: chosen, note }); toast.success("Logged. Be gentle with yourself."); setPicking(false); setNote(""); setChosen(null); load(); }
    catch { toast.error("Could not log"); }
  };

  const deleteMood = async (moodId) => {
    if (!window.confirm("Delete this private mood check-in?")) return;
    try {
      await api.delete(`/wellness/mood/${moodId}`);
      toast.success("Mood check-in deleted");
      load();
    } catch {
      toast.error("Could not delete check-in");
    }
  };

  return (
    <div data-testid="wellness-page">
      <h1 className="text-4xl">Wellness</h1>
      <p className="text-muted-foreground mt-1">A calmer feed, on your terms.</p>

      <div className="mt-6 grid md:grid-cols-3 gap-5">
        <div className="brutal-card bg-accent p-6 md:col-span-2 flex items-center gap-4">
          <Heart size={36} strokeWidth={2.5} />
          <div className="flex-1">
            <h3 className="text-2xl">How are you, really?</h3>
            <p className="text-sm">Tiny check-in. No judgment.</p>
          </div>
          <button onClick={() => setPicking(true)} className="brutal-btn bg-card px-4 py-2" data-testid="checkin-btn">Check in</button>
        </div>

        <div className="brutal-card bg-sage p-6">
          <Wind size={28} strokeWidth={2.5} />
          <h3 className="text-2xl mt-2">Mindful break</h3>
          <p className="text-sm">60 seconds of breath</p>
          <button onClick={() => setBreath(true)} className="brutal-btn bg-card mt-3 px-3 py-1 text-sm" data-testid="breath-btn">Start</button>
        </div>

        <div className="brutal-card p-6 md:col-span-2 bg-card">
          <div className="flex items-center gap-2 mb-2"><Sparkles size={18} strokeWidth={2.5} /> <h3 className="text-xl">Care reflection</h3></div>
          <p className="text-sm leading-relaxed">{data?.insight || "Log a mood to receive a kind reflection."}</p>
          <p className="text-xs text-muted-foreground mt-2">Generated privately from mood labels. Your notes are not sent to an AI provider.</p>
          <div className="mt-3 brutal-card bg-secondary p-3 text-sm"><b>Tip:</b> {data?.tip_of_day}</div>
        </div>

        <div className="brutal-card overflow-hidden p-0">
          <img src={NATURE_IMG} alt="Peaceful outdoor landscape" className="w-full h-40 object-cover" />
          <div className="p-4">
            <h4 className="text-lg">Step outside</h4>
            <p className="text-xs text-muted-foreground">A 5-minute walk shifts more than a 50-minute scroll.</p>
          </div>
        </div>

        <div className="brutal-card p-6 bg-card md:col-span-3">
          <h3 className="text-xl mb-3">Your last week of moods</h3>
          {data?.moods?.length ? (
            <div className="flex flex-wrap gap-2">
              {data.moods.map((m) => {
                const md = MOODS.find((x) => x.key === m.mood);
                const Icon = md?.Icon || Meh;
                return (
                  <div key={m.id} className={`brutal-card px-3 py-2 text-xs ${md?.bg || "bg-card"} flex items-center gap-2`}>
                    <Icon size={14} strokeWidth={2.5} />
                    <span>{m.mood} · {new Date(m.created_at).toLocaleDateString()}</span>
                    <button
                      type="button"
                      onClick={() => deleteMood(m.id)}
                      aria-label={`Delete ${m.mood} mood check-in from ${new Date(m.created_at).toLocaleDateString()}`}
                      className="ml-1 p-1 hover:bg-card"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-sm text-muted-foreground">Nothing logged yet.</p>}
        </div>
      </div>

      {picking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="mood-dialog-title">
          <button type="button" aria-label="Close mood check-in" className="absolute inset-0 bg-foreground/60" onClick={() => setPicking(false)} />
          <div className="relative z-10 brutal-card bg-card p-6 max-w-md w-full" data-testid="mood-modal">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 id="mood-dialog-title" className="text-2xl">How are you feeling?</h3>
              <button type="button" aria-label="Close mood check-in" onClick={() => setPicking(false)} className="brutal-btn bg-card p-1"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-5 gap-2 mb-3">
              {MOODS.map(({ key, label, Icon, bg }) => (
                <button key={key} onClick={() => setChosen(key)} data-testid={`mood-${key}`}
                  aria-pressed={chosen === key}
                  className={`brutal-btn p-3 ${bg} ${chosen === key ? "shadow-none translate-x-[2px] translate-y-[2px]" : ""}`}>
                  <Icon size={20} strokeWidth={2.5} className="mx-auto" />
                  <div className="text-xs mt-1">{label}</div>
                </button>
              ))}
            </div>
            <textarea aria-label="Optional mood note" placeholder="A note for future-you (optional)" rows={2} className="brutal-input w-full px-3 py-2 text-sm"
              value={note} onChange={(e) => setNote(e.target.value)} data-testid="mood-note" />
            <button onClick={submitMood} disabled={!chosen} className="brutal-btn bg-primary text-primary-foreground w-full py-2 mt-3 disabled:opacity-50" data-testid="mood-save">Save</button>
          </div>
        </div>
      )}

      {breath && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="breath-dialog-title" data-testid="breath-modal">
          <button type="button" aria-label="Close mindful breathing" className="absolute inset-0 bg-foreground/80" onClick={() => setBreath(false)} />
          <div className="relative z-10 text-center text-background">
            <button type="button" aria-label="Close mindful breathing" onClick={() => setBreath(false)} className="absolute -top-10 right-0 p-2"><X size={22} /></button>
            <div className="mx-auto w-40 h-40 rounded-full bg-primary border-2 border-background animate-[ping_4s_ease-in-out_infinite]" aria-hidden="true" />
            <p id="breath-dialog-title" className="mt-6 font-display text-3xl">Breathe in… and out.</p>
            <p className="text-sm opacity-80 mt-2">Press Escape or use the close control when you're ready.</p>
          </div>
        </div>
      )}
    </div>
  );
}
